import React, { useEffect, useState } from 'react';
import {
  Button,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Autocomplete,
  Card
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import PropTypes from 'prop-types';

import axiosInstance from '../../api';
import SERVER_API_URL from '../../config';
import {
  INTERNAL_SERVER_ERROR,
  REQUIRED_FIELD,
} from '../../utils/exception';

export default function NewConfessor({ userToBeUpdated, setUserToBeUpdated, updater, handleNext }) {

  const [selectedConfessor, setSelectedConfessor] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [firstDisabled, setFirstDisabled] = useState(true);

  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [confessors, setConfessors] = useState([]);

  const [confessorIdClicked, setConfessorIdClicked] = useState(false);
  const [formErrorOnConfessorId, setFormErrorOnConfessorId] = useState(false);
  const [helperTextOnConfessorId, setHelperTextOnConfessorId] = useState(null);

  const handleRegistrationStep = async () => {
    try {
      const thisUser = await axiosInstance.post(`${SERVER_API_URL}/user/findOneById/${userToBeUpdated.id}`);
      if (thisUser.status === 200) {
        setUserToBeUpdated(thisUser.data);
        handleNext();
      } else {
        setFormError('የዋስትና መረጃ በትክክል አልተዘጋጀም!');
        setFormErrorSeverity('error');
      }
    } catch (error) {
      setFormError(`የዋስትና መረጃ በትክክል አልተዘጋጀም..!`);
      setFormErrorSeverity('error');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormErrorSeverity('error');
    setDisabled(true);

    if (!formErrorOnConfessorId) {
      try {
        const response = await axiosInstance.patch(
          `${SERVER_API_URL}/user/confessorChange/${userToBeUpdated && userToBeUpdated.id}/${updater && updater.id}/${selectedConfessor && selectedConfessor.id}`,
        );

        if (response.status === 200) {
          setFormError('የንስሃ አባት መረጃ በተሳካ ሁኔታ ተቀምጧል!');
          setFormErrorSeverity('success');

          setUserToBeUpdated(response.data); 
          handleNext();
        }
      } catch (e) {
        if (e.response && e.response.status) {
          if (e.response.status === 404 || e.response.status === 400 || e.response.status === 401 || e.response.status === 403 || e.response.status === 406) {
            setFormError(e.response.data.message);
          } else if (e.response.status === 500) {
            setFormError(INTERNAL_SERVER_ERROR);
          }
        }
      }
    } else {
      setFormError('እባክዎ አስፈላጊዎቹን መስኮች ይሙሉ!');
    }
    setDisabled(false);
    setFirstDisabled(true);
  };

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');
    setFirstDisabled(false);

    const { name, value } = e.target;
    switch (name) {
      case 'confessorId':
        if (value.length === 0) {
          setConfessorIdClicked(false);
          setFormErrorOnConfessorId(confessorIdClicked && value.length === 0);
          setHelperTextOnConfessorId(REQUIRED_FIELD);
        } else {
          setHelperTextOnConfessorId(null);
          setFormErrorOnConfessorId(false);
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const getConfessor = async () => {
      try {
        const response = await axiosInstance.post(`${SERVER_API_URL}/user/confessorsByChurchId/${userToBeUpdated && userToBeUpdated.church.id}`)
        if (response.status === 200) {
          setConfessors(response.data);
        }
      } catch (e) {
        // console.log(e)
      }
    }
    getConfessor();
  }, [userToBeUpdated]);

  const handleClearForm = () => {

    setFormError(null);
    setFormErrorSeverity('error');
    setDisabled(false);
    setFirstDisabled(true);

    setConfessorIdClicked(false);
    setFormErrorOnConfessorId(false);
    setHelperTextOnConfessorId(null);
  };

  return (
    <Card sx={{ padding: { xs: 1, sm: 2 }, backgroundColor: (theme) => theme.palette.mode === 'light' ? '#FEFEFE' : '#1F1F1F' }} component="form" autoComplete="off" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {formError && (
          <Grid item xs={12} sm={12}>
            <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
              {formError}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12} sm={6} lg={4} xl={3}>
          <Autocomplete
            freeSolo
            value={selectedConfessor}
            options={confessors}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.category && option.category?.name})`}
            onChange={(event, newValue) => {
              setSelectedConfessor(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={formErrorOnConfessorId}
                name="confessorId"
                label="የንስሃ አባት ስም"
                // variant="standard"
                onChange={handleFormValueBlur}
                helperText={formErrorOnConfessorId ? helperTextOnConfessorId : null}
                onFocus={() => setConfessorIdClicked(true)}
                onBlur={handleFormValueBlur}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right', mt: 1 }}>
          <Button sx={{ width: 150 }} onClick={handleRegistrationStep}>
            ዝለል
          </Button>
          <Button
            color="error"
            type="reset"
            onClick={handleClearForm}
            sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
          >
            <RefreshIcon /> ቅጽ ዳግም አስጀምር
          </Button>{' '}
          {disabled && (
            <Button variant="contained" type="submit" sx={{ minWidth: { xs: 80, sm: 150 } }} disabled={disabled}>
              <CircularProgress style={{ width: '20px', height: '20px' }} />
              በማስቀመጥ ላይ ...
            </Button>
          )}
          {!disabled && (
            <Button
              variant="contained"
              type="submit"
              startIcon={<SaveTwoToneIcon />}
              disabled={disabled || firstDisabled}
              sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
            >
              ለውጦችን አስቀምጥ
            </Button>
          )}
        </Grid>
      </Grid>
    </Card>
  );
}

NewConfessor.propTypes = {
  userToBeUpdated: PropTypes.shape({
    id: PropTypes.string.isRequired,
    church: { id: PropTypes.string.isRequired },
  }),
  setUserToBeUpdated: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  notification: PropTypes.shape({
    message: PropTypes.string.isRequired,
    severity: PropTypes.string.isRequired,
  }),
  updater: PropTypes.shape({
    id: PropTypes.string.isRequired,
    role: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
  }),
};
